import { meaningKey } from "../core/model/grade.ts";
import { canonical, hasTones, markSyllable, TONES, toneMarks, type Tone } from "../core/model/pinyin.ts";
import { sayPinyin } from "../core/model/say-pinyin.ts";
import { ANSWER_MAX, type Card, type Kind } from "../core/model/types.ts";

export interface Result {
  correct: boolean;
  given: string;
}

// What the card shows about saving its answer. Next card waits while saving or failed.
export type SaveView =
  | { readonly tag: "saving" }
  | { readonly tag: "failed"; readonly detail: string; readonly hint: string }
  | { readonly tag: "conflicted"; readonly note: string }
  | { readonly tag: "confirmed"; readonly regraded: boolean };

export const saveLocks = (save: SaveView | null): boolean => save?.tag === "saving" || save?.tag === "failed";

export interface FlashcardView {
  label: string;
  wantsPinyin: boolean;
  placeholder: string;
  maxLength: number;
  chars: number | null;
  shape: (string | null)[];
  shapeLabel: string;
  alternatives: string[];
  // Result reading; absent on recall cards and meaning cards without pinyin.
  reading: string | null;
  speak: string | null;
}

const HAN = /\p{Script=Han}/u;
const LABEL: Record<Kind, string> = { meaning: "Meaning", pinyin: "Reading", recall: "Recall" };

export const wantsPinyin = (kind: Kind): boolean => kind === "pinyin" || kind === "recall";

// Characters that speak a wrong pinyin answer as typed; null when right, not pinyin, or unspeakable.
export const sayGiven = (card: Card, result: Result | null): string | null =>
  result && !result.correct && wantsPinyin(card.kind) ? sayPinyin(result.given) : null;

export function formatInput(kind: Kind, raw: string): string {
  return wantsPinyin(kind) ? toneMarks(raw) : raw;
}

export interface ToneEdit {
  start: number;
  end: number;
  text: string;
}

const LETTERS_BEFORE = /[a-zü:]+$/i;

// Replace the preceding letters and selection with their tone-marked form.
export function toneEdit(value: string, from: number, to: number, digit: string): ToneEdit | null {
  if (!/^[0-5]$/.test(digit)) return null;
  const run = LETTERS_BEFORE.exec(value.slice(0, from))?.[0];
  if (!run || !/[a-zü]/i.test(run)) return null;
  return { start: from - run.length, end: to, text: toneMarks(run + digit) };
}

// Touch pinyin answers: letters first, then one tone per space-separated syllable.
export interface ToneSlot {
  readonly letters: string;
  readonly tone: Tone | null;
}

export interface ToneStep {
  readonly slots: readonly [ToneSlot, ...ToneSlot[]];
  // The highlighted syllable; null once every syllable has a tone.
  readonly current: number | null;
}

export interface ToneChoice {
  readonly tone: Tone;
  readonly label: string;
}

// Toneless letters lead to the tone step; text with any tone is graded as typed.
export const needsTones = (text: string): boolean => !hasTones(text);

export function toneStep(letters: string): ToneStep | null {
  const [first, ...rest] = letters.trim().split(/\s+/).filter(Boolean);
  if (first === undefined) return null;
  const slot = (l: string): ToneSlot => ({ letters: l, tone: null });
  return { slots: [slot(first), ...rest.map(slot)], current: 0 };
}

export const toneChoices = (letters: string): ToneChoice[] => TONES.map((tone) => ({ tone, label: markSyllable(letters, tone) }));

// Picking a syllable's tone moves on to the next empty one; picking its tone again just reselects it.
export function pickTone(step: ToneStep, at: number, tone: Tone): ToneStep {
  const slot = step.slots[at];
  if (!slot) return step;
  if (slot.tone === tone) return { ...step, current: at };
  const slots: [ToneSlot, ...ToneSlot[]] = [...step.slots];
  slots[at] = { ...slot, tone };
  const after = slots.findIndex((s, i) => i > at && s.tone === null);
  const next = after < 0 ? slots.findIndex((s) => s.tone === null) : after;
  return { slots, current: next < 0 ? null : next };
}

// "ni3 hao3" once every syllable has a tone.
export function toneAnswer(step: ToneStep): string | null {
  return step.slots.some((s) => s.tone === null) ? null : step.slots.map((s) => s.letters + s.tone).join(" ");
}

export const tonePreview = (step: ToneStep): string =>
  step.slots.map((s) => (s.tone === null ? s.letters : markSyllable(s.letters, s.tone))).join(" ");

function alternatives(card: Card, pinyin: boolean): string[] {
  const key = pinyin ? canonical : meaningKey;
  const seen = new Set([key(card.answer)]);
  const out: string[] = [];
  for (const a of card.accepted ?? []) {
    const k = key(a);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(pinyin ? toneMarks(a) : a);
  }
  return out;
}

export function flashcardView(card: Card): FlashcardView {
  const pinyin = wantsPinyin(card.kind);
  const shape = card.kind === "recall" && card.word ? [...card.word].map((c) => (HAN.test(c) ? null : c)) : [];
  const han = shape.filter((c) => c === null).length;
  return {
    label: LABEL[card.kind],
    wantsPinyin: pinyin,
    placeholder: pinyin ? "pinyin, tones as numbers" : "meaning",
    // TODO: Count code points at the input edge; native maxlength counts UTF-16 units, unlike Answer.
    maxLength: ANSWER_MAX,
    chars: card.kind === "recall" ? null : [...card.prompt].length,
    shape,
    shapeLabel: `${han} ${han === 1 ? "character" : "characters"}`,
    alternatives: alternatives(card, pinyin),
    reading: card.kind === "meaning" ? card.pinyin || null : card.kind === "pinyin" ? card.answer : null,
    speak: card.kind === "meaning" ? null : card.say || (card.kind === "pinyin" ? card.prompt : card.word || null),
  };
}
